import { CDN_BASE_URL } from '@variables';

const DEFAULT_IMAGE_FORMAT = 'image/webp';

export interface ImageResizeOption {
  maxWidth?: number;
  maxHeight?: number;
  format?: string;
  /** 0–1 for JPEG / WebP `canvas.toBlob`. Default 0.92 when output is jpeg/webp. Ignored for PNG. */
  quality?: number;
}

export interface ImageSplitOption {
  height?: number;
  format?: string;
}

export interface ImageResizeResult {
  image: Blob;
  type: string;
}

export const resizeImage = async (imageBlob: Blob, options?: ImageResizeOption): Promise<ImageResizeResult> => {
  if (!imageBlob.type.startsWith('image')) {
    return await new Promise<ImageResizeResult>((resolve) => {
      resolve({ image: imageBlob, type: imageBlob.type });
    });
  }
  const { maxWidth, maxHeight, format, quality } = options || {};

  // No resize and no explicit output format → keep original bytes (avoids canvas WebP re-encode quality loss).
  if (!maxWidth && !maxHeight && !format) {
    return { image: imageBlob, type: imageBlob.type };
  }

  const imageFormat = format || DEFAULT_IMAGE_FORMAT;

  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');
  const URL = window.URL || window.webkitURL;
  const objectUrl = URL.createObjectURL(imageBlob);
  const resultImageBlob = await new Promise<ImageResizeResult>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      let ratio = 1;
      let targetWidth = image.width;
      let targetHeight = image.height;

      if (maxWidth && image.width > maxWidth) {
        ratio = maxWidth / image.width;
      }

      if (maxHeight && image.height > maxHeight) {
        const heightRatio = maxHeight / image.height;
        ratio = Math.min(ratio, heightRatio);
      }

      targetWidth = Math.floor(((image.width * ratio) / 4) * 4);
      targetHeight = Math.floor(((image.height * ratio) / 4) * 4);

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      if (imageFormat === DEFAULT_IMAGE_FORMAT && (targetWidth > 16383 || targetHeight > 16383)) {
        let data = { property: 'width', originSize: image.width, expectedSize: 16382 / ratio };

        if (targetHeight > 16383) {
          data = { property: 'height', originSize: image.height, expectedSize: Math.floor((16382 / ratio / 4) * 4) };
        }
        reject({ code: 'TOO_LARGE_IMAGE', ...data });
      }

      if (canvasContext) {
        canvasContext.drawImage(image, 0, 0, targetWidth, targetHeight);
        const mimeForQuality = imageFormat === 'image/jpg' ? 'image/jpeg' : imageFormat;
        const encodeQuality =
          quality !== undefined
            ? quality
            : mimeForQuality === 'image/webp' || mimeForQuality === 'image/jpeg'
              ? 0.92
              : undefined;
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ image: blob, type: blob.type });
            } else {
              resolve({ image: imageBlob, type: imageBlob.type });
            }
          },
          imageFormat,
          encodeQuality,
        );
      } else {
        reject({ code: 'CANNOT_GET_CANVAS_CONTEXT' });
      }
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = (error) => {
      reject(error);
    };

    image.src = objectUrl;
  });

  return resultImageBlob;
};

/** Crop area in pixels (e.g. from react-easy-crop onCropComplete croppedAreaPixels) */
export interface CropAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Returns a blob of the cropped region of the image.
 * @param imageUrl - Object URL or URL of the image
 * @param cropAreaPixels - Crop area in pixels (e.g. from react-easy-crop croppedAreaPixels)
 * @param imageType - Optional MIME type for output (default: image/jpeg)
 */
export const getCroppedImageBlob = async (
  imageUrl: string,
  cropAreaPixels: CropAreaPixels,
  imageType: string = 'image/jpeg',
): Promise<Blob> => {
  const image = await getImageElement(imageUrl);
  const { x, y, width, height } = cropAreaPixels;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('CANNOT_GET_CANVAS_CONTEXT');
  }
  ctx.drawImage(image, x, y, width, height, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      imageType,
      0.92,
    );
  });
};

export const getImageElement = async (url: string) => {
  const result = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve(image);

      // URL.revokeObjectURL(url);
    };

    image.onerror = (error) => {
      reject(error);
    };

    image.src = url;
  });
  return result;
};

export const splitImage = async (imageBlob: Blob, options?: ImageSplitOption): Promise<ImageResizeResult[]> => {
  const { height, format } = options || {};
  const imageFormat = format || 'image/png';
  const URL = window.URL || window.webkitURL;
  const objectUrl = URL.createObjectURL(imageBlob);
  const imageElement = await getImageElement(objectUrl);
  const originWidth = imageElement.width;
  const originHeight = imageElement.height;
  const targetHeight = height || 1000;
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');

  // 이미지가 targetHeight보다 작은 경우 분할하지 않고 그대로 반환
  if (originHeight <= targetHeight) {
    URL.revokeObjectURL(objectUrl);
    return [{ image: imageBlob, type: imageBlob.type }];
  }

  let totalLength = Math.ceil(originHeight / targetHeight);
  const lastHeight = originHeight % targetHeight;

  // 마지막 조각이 너무 작으면 이전 조각과 합치기
  if (lastHeight < 500 && totalLength > 1) {
    totalLength = totalLength - 1;
  }

  // 최소 1개는 보장
  totalLength = Math.max(totalLength, 1);

  const results: ImageResizeResult[] = [...new Array(totalLength)];

  for (let i = 0; i < results.length; i++) {
    const resultImageBlob = await new Promise<ImageResizeResult>((innerResolve, innerReject) => {
      const y = i * targetHeight;
      let canvasHeight: number;
      let sourceHeight: number;

      if (i === results.length - 1) {
        // 마지막 조각인 경우
        const remainingHeight = originHeight - y;

        if (lastHeight < 500 && totalLength > 1) {
          // 마지막 조각이 너무 작아서 이전 조각과 합쳐진 경우
          canvasHeight = targetHeight + lastHeight;
          sourceHeight = remainingHeight;
        } else {
          // 일반적인 마지막 조각
          canvasHeight = remainingHeight;
          sourceHeight = remainingHeight;
        }
      } else {
        // 중간 조각들
        canvasHeight = targetHeight;
        sourceHeight = targetHeight;
      }

      canvas.width = originWidth;
      canvas.height = canvasHeight;

      if (canvasContext) {
        canvasContext.clearRect(0, 0, canvas.width, canvas.height);

        // drawImage: (image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
        canvasContext.drawImage(imageElement, 0, y, originWidth, sourceHeight, 0, 0, originWidth, canvasHeight);
        canvas.toBlob((blob) => {
          if (blob) {
            innerResolve({ image: blob, type: blob.type });
          }
        }, imageFormat);
      } else {
        innerReject({ code: 'CANNOT_GET_CANVAS_CONTEXT' });
      }
    });
    results[i] = resultImageBlob;
  }

  URL.revokeObjectURL(objectUrl);
  return results;
};

const CLOUDINARY_DIRECTORIES = [
  '/mainHeaderImages/',
  '/blog-covers/',
  '/chatting/',
  '/edufessional-programs/',
  '/edufessionals/',
  '/magazineImages/',
  '/notices/',
  '/profileimage/',
  '/program-images/',
  '/report/',
  '/teacher-contract/',
  '/tutorcertificate/',
  '/image/jpeg/',
];

export const cdnImageUrl = (url?: string | null) => {
  if (!url) {
    return undefined;
  }

  if (url.startsWith('https://res.cloudinary.com/igo2igo') || url.startsWith('http://res.cloudinary.com/igo2igo')) {
    const found = CLOUDINARY_DIRECTORIES.find((dir) => url.includes(dir));
    if (found) {
      const fileNames = url.split(found);
      return `${CDN_BASE_URL}/cloudinary${found}${fileNames[1]}`;
    }
  }

  if (
    url.startsWith('blob') ||
    url.startsWith('http') ||
    url.startsWith('data:image') ||
    url.startsWith('/static/media')
  ) {
    return url;
  }

  return `${CDN_BASE_URL}${url}`;
};
